// ===========================================
// Student Fun Mode Wrapper Component
// ===========================================
// Wraps settings content with fun mode UI for students

import { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../../contexts/TenantContext';
import { Box, Button, Container, Divider, Paper, Typography } from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Email as EmailIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  StudentAvatar,
  LayoutToggle,
  FunHeader,
  FunDecorations,
  FunUserMenu,
} from '../../student';
import {
  getAgeGroupFromBirthDate,
  getCharacterColor,
  getCharacterName,
  AgeGroup,
} from '../../brand';
import { CARD_COLORS } from './account.constants';

interface StudentProfile {
  id: string;
  firstName: string;
  lastName: string;
  birthDate: string | null;
  avatar?: string | null | undefined;
}

interface Props {
  studentProfile: StudentProfile;
  children: ReactNode;
  onLogout: () => Promise<void>;
}

export default function StudentFunModeWrapper({
  studentProfile,
  children,
  onLogout,
}: Props) {
  const navigate = useNavigate();
  const { schoolSlug } = useTenant();
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);

  // Calculate character color for fun mode
  const ageGroup: AgeGroup = studentProfile.birthDate
    ? getAgeGroupFromBirthDate(studentProfile.birthDate)
    : 'KIDS';
  const avatarCharacterMap: Record<string, AgeGroup> = {
    alice: 'PRESCHOOL',
    steve: 'KIDS',
    liam: 'TEENS',
    floyd: 'ADULT',
  };
  const avatarCharacter = studentProfile.avatar?.split('/')[0] || '';
  const avatarAgeGroup = avatarCharacterMap[avatarCharacter] || ageGroup;
  const characterColor = getCharacterColor(avatarAgeGroup);
  const characterName = getCharacterName(avatarAgeGroup);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = async () => {
    handleUserMenuClose();
    await onLogout();
    navigate('/login');
  };

  return (
    <Box
      sx={{
        bgcolor: '#FCF6E6',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        mx: -3,
        mt: -3,
        px: 3,
        pt: 1,
      }}
    >
      {/* Fun mode decorations */}
      <FunDecorations characterName={characterName} />

      {/* Fun Header (Piano) with user menu */}
      <FunHeader
        firstName={studentProfile.firstName}
        characterColor={characterColor}
        userMenuButton={
          <FunUserMenu
            studentProfile={{
              ...studentProfile,
              avatar: studentProfile.avatar ?? null,
              birthDate: studentProfile.birthDate ?? '',
            }}
            characterColor={characterColor}
            anchorEl={userMenuAnchor}
            onOpen={handleUserMenuOpen}
            onClose={handleUserMenuClose}
            onLogout={handleLogout}
            onNavigate={navigate}
          />
        }
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pb: 4 }}>
        {/* Student Header Bar */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            bgcolor: CARD_COLORS.header.bg,
            borderRadius: 4,
            border: `3px solid ${CARD_COLORS.header.border}`,
            boxShadow: '4px 4px 0px rgba(0,0,0,0.08)',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {/* Avatar */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'background.paper',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 3,
              flexShrink: 0,
              border: `3px solid ${CARD_COLORS.header.border}`,
            }}
          >
            <StudentAvatar
              avatar={studentProfile.avatar ?? null}
              birthDate={studentProfile.birthDate ?? ''}
              firstName={studentProfile.firstName}
              lastName={studentProfile.lastName}
              size={70}
            />
          </Box>

          {/* Name and Info */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              fontWeight="bold"
              sx={{
                fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive',
                color: characterColor,
              }}
            >
              {studentProfile.firstName} {studentProfile.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Account Settings
            </Typography>
          </Box>

          {/* Navigation and Layout Toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Dashboard Button */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<DashboardIcon />}
              onClick={() => navigate(`/${schoolSlug}/student`)}
              sx={{
                borderColor: characterColor,
                color: characterColor,
                '&:hover': {
                  borderColor: characterColor,
                  bgcolor: `${characterColor}20`,
                },
                fontWeight: 600,
                borderRadius: 2,
              }}
            >
              Dashboard
            </Button>

            {/* Messages Button */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<EmailIcon />}
              onClick={() => navigate(`/${schoolSlug}/student/messages`)}
              sx={{
                borderColor: characterColor,
                color: characterColor,
                '&:hover': {
                  borderColor: characterColor,
                  bgcolor: `${characterColor}20`,
                },
                fontWeight: 600,
                borderRadius: 2,
              }}
            >
              Messages
            </Button>

            {/* Settings Button (active) */}
            <Button
              variant="contained"
              size="small"
              startIcon={<SettingsIcon />}
              sx={{
                bgcolor: characterColor,
                color: 'white',
                '&:hover': {
                  bgcolor: characterColor,
                  filter: 'brightness(0.9)',
                },
                fontWeight: 600,
                borderRadius: 2,
              }}
            >
              Settings
            </Button>

            {/* Divider */}
            <Divider
              orientation="vertical"
              flexItem
              sx={{ mx: 1, borderColor: `${characterColor}40` }}
            />

            {/* Layout Toggle */}
            <LayoutToggle showLabel={false} />
          </Box>
        </Paper>

        {/* Settings Content */}
        {children}
      </Container>
    </Box>
  );
}
